import * as React from 'react'

import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'cva'

import Spinner from '@/components/icons/spinner'

import { cx } from '@/libs/cva'

const iconButtonVariants = cva({
	base: cx(
		'relative inline-flex w-fit items-center justify-center overflow-hidden rounded-md outline-none transition-fg',
		'disabled:bg-ui-bg-disabled disabled:text-ui-fg-disabled disabled:shadow-buttons-neutral disabled:after:hidden'
	),
	variants: {
		variant: {
			primary: cx(
				'after:button-neutral-gradient bg-ui-button-neutral text-ui-fg-subtle shadow-buttons-neutral',
				'hover:after:button-neutral-hover-gradient hover:bg-ui-button-neutral-hover',
				'active:after:button-neutral-pressed-gradient active:bg-ui-button-neutral-pressed',
				'focus-visible:shadow-buttons-neutral-focus',
				"after:absolute after:inset-0 after:content-['']"
			),
			transparent: cx(
				'bg-ui-button-transparent text-ui-fg-subtle',
				'hover:bg-ui-button-transparent-hover',
				'active:bg-ui-button-transparent-pressed',
				'focus-visible:bg-ui-bg-base focus-visible:shadow-buttons-neutral-focus',
				'disabled:!bg-transparent disabled:!shadow-none'
			),
			trash:
				'bg-ui-button-danger text-ui-fg-on-color hover:bg-ui-button-danger-hover',
		},
		size: {
			'2xsmall': 'h-5 w-5',
			xsmall: 'h-6 w-6 p-1',
			small: 'h-7 w-7 p-1',
			base: 'h-8 w-8 p-1.5',
			large: 'h-10 w-10 p-2.5',
			xlarge: 'h-12 w-12 p-3.5',
		},
	},
	defaultVariants: {
		variant: 'primary',
		size: 'base',
	},
})

interface IconButtonProps
	extends React.ComponentPropsWithoutRef<'button'>,
		VariantProps<typeof iconButtonVariants> {
	asChild?: boolean
	isLoading?: boolean
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
	(
		{
			variant = 'primary',
			size = 'base',
			asChild = false,
			className,
			children,
			isLoading = false,
			disabled,
			...props
		}: IconButtonProps,
		ref
	) => {
		const Component = asChild ? Slot : 'button'

		const renderInner = () => {
			if (isLoading) {
				return (
					<span className="pointer-events-none">
						<div
							className={cx(
								'absolute inset-0 flex items-center justify-center rounded-md bg-ui-bg-disabled'
							)}
						>
							<Spinner className="animate-spin" />
						</div>
						{children}
					</span>
				)
			}

			return children
		}

		return (
			<Component
				ref={ref}
				{...props}
				className={cx(iconButtonVariants({ variant, size }), className)}
				disabled={disabled || isLoading}
			>
				{renderInner()}
			</Component>
		)
	}
)
IconButton.displayName = 'IconButton'

export { IconButton, iconButtonVariants }
